import { useCallback } from 'react';
import { fetchPagedReq } from 'shared/api/axios';
import { AuditEntityType, ContractDto, ContractInfoDto, CreateContractDto, IPagingData, UpdateContractDto } from '@sr/dto';
import { plainToInstance } from 'class-transformer';
import { FormValues } from 'components/Contracts/contract.form';
import { useRemoteItem } from 'utils/remote-item.hook';
import { authorizedRequest } from 'shared/api/axios';
import { CONTRACTS_BASE_URL } from 'shared/api/api';
import { useReadOnlyRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { invalidateEntityAudit } from 'entities/audit/utils';
import { useAppDispatch } from 'shared/store';

export const useContractsList = (paging: IPagingData, filterString?: string, companyId?: number) => {
  const collection = useReadOnlyRemotePagedCollection<ContractInfoDto>({
    fetchHandler: useCallback(async () => {
      const params = new URLSearchParams(filterString);
      let url = CONTRACTS_BASE_URL;
      if (companyId){
        url += `/company/${companyId}`;
      }

      return await fetchPagedReq<void, ContractInfoDto>(`${url}?${params.toString()}`, paging, ContractInfoDto, params);
    }, [companyId, filterString, paging])
  });

  const postHandler = useCallback(async (formData: FormValues, companyId: number) => {
    const dto = plainToInstance<CreateContractDto, FormValues>(CreateContractDto, formData);
    const response = await authorizedRequest<CreateContractDto, ContractDto>(`${CONTRACTS_BASE_URL}/${companyId}`, 'POST', dto);

    return plainToInstance(ContractInfoDto, response, { enableImplicitConversion: true });
  }, []);

  const deleteHandler = useCallback(async (id: number) => authorizedRequest<void, void>(`${CONTRACTS_BASE_URL}/${id}`, 'DELETE'), []);

  return {
    collection,
    addContract: postHandler,
    deleteContract: deleteHandler
  };
};

export const useContract = (contractId: number) => {
  const dispatch = useAppDispatch();
  const remoteItem = useRemoteItem<ContractDto, UpdateContractDto>({
    fetchHandler: useCallback(async () => {
      const response = await authorizedRequest<void, ContractDto>(`${CONTRACTS_BASE_URL}/${contractId}`, 'GET');
      return plainToInstance(ContractDto, response, { enableImplicitConversion: true });
    }, [contractId]),

    updateHandler: async (data) => {
      const response = await authorizedRequest<UpdateContractDto, ContractDto>(`${CONTRACTS_BASE_URL}`, 'PUT', data);
      invalidateEntityAudit(dispatch, AuditEntityType.CONTRACT, data.id ?? contractId);
      return plainToInstance(ContractDto, response, { enableImplicitConversion: true });
    }
  });

  const deactivateItem = async () => {
    await authorizedRequest<void, ContractDto>(`${CONTRACTS_BASE_URL}/${contractId}/deactivate`, 'PUT');
    invalidateEntityAudit(dispatch, AuditEntityType.CONTRACT, contractId);
    await remoteItem.refreshItem();
  };

  const activateItem = async () => {
    await authorizedRequest<void, ContractDto>(`${CONTRACTS_BASE_URL}/${contractId}/activate`, 'PUT');
    invalidateEntityAudit(dispatch, AuditEntityType.CONTRACT, contractId);
    await remoteItem.refreshItem();
  };

  return { ...remoteItem, deactivateItem, activateItem };
};
